import React, { memo, useMemo } from 'react';
import { Pressable } from 'react-native';

import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import type { CoGuardianMutualKidVM } from '@/features/guardian-player/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const AVATAR_SIZE = 60;

type MutualKidCardProps = {
  kid: CoGuardianMutualKidVM;
  onPress: (kid: CoGuardianMutualKidVM) => void;
};

/** Tappable mutual-kid card → opens the Player Profile Detail sheet. */
function MutualKidCard({ kid, onPress }: MutualKidCardProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  const avatarSource = useMemo(
    () => (kid.avatarUrl ? { uri: kid.avatarUrl } : imgUser),
    [kid.avatarUrl],
  );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={kid.name}
      onPress={() => onPress(kid)}
      style={({ pressed }) => [styles.card, pressed ? styles.pressed : null]}>
      <Stack
        align="center"
        justify="center"
        width={scale(AVATAR_SIZE)}
        height={scale(AVATAR_SIZE)}
        borderRadius={scale(AVATAR_SIZE / 2)}
        backgroundColor={guardianColors['8']}
        overflow="hidden">
        <Image
          source={avatarSource}
          width={scale(AVATAR_SIZE)}
          height={scale(AVATAR_SIZE)}
          circular
          resizeMode="cover"
        />
      </Stack>

      <Stack flex={1} style={styles.info}>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
          numberOfLines={1}
          style={styles.name}>
          {kid.name}
        </Text>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
          numberOfLines={1}
          style={styles.meta}>
          {kid.metaLabel}
        </Text>
        {kid.sinceLabel ? (
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            numberOfLines={1}
            style={styles.since}>
            {kid.sinceLabel}
          </Text>
        ) : null}
      </Stack>

      <SvgIcon name="IcChevronRight" size={scale(20)} color={guardianColors['6']} />
    </Pressable>
  );
}

export default memo(MutualKidCard);

const useStyles = createStyles(({ scale }) => ({
  card: {
    // Full width of the column — NOT flex:1, which collapses to flexBasis:0 on web and
    // squeezes the text column to nothing (leaving only the avatar + chevron).
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(10),
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
  },
  pressed: {
    opacity: 0.7,
  },
  info: {
    // Allow the text column to shrink/grow within the row instead of overflowing.
    minWidth: 0,
  },
  name: {
    color: guardianColors['10'],
  },
  meta: {
    color: guardianColors['Main/Main'],
  },
  since: {
    color: guardianColors['6'],
  },
}));
